import { MagnifyingGlassIcon, XIcon } from "@/frontend/shared/assets/icons";
import styles from "./SearchInput.module.scss";
import clsx from "clsx";
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { Textarea } from "../Textarea/Textarea";

type TSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  compRef?: RefObject<HTMLDivElement>;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onClose?: () => void;
  isTextarea?: boolean;
  inputId?: string;
};

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  compRef,
  onClick,
  onKeyDown,
  onFocus,
  onClose,
  isTextarea = false,
  inputId
}: TSearchInputProps) {
  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  if (isTextarea) {
    const resizeTextarea = useCallback((textarea: HTMLTextAreaElement) => {
      // Reset height to recalculate for shrinking
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, []);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
      if (textareaRef.current) {
        resizeTextarea(textareaRef.current);
      }
    }, [value, resizeTextarea]);

    const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      resizeTextarea(e.target);
      onChange(e.target.value);
    };

    return (
      <div className={clsx(styles.container, className)} ref={compRef} onClick={(e) => onClick?.(e)}>
        <Textarea
          ref={textareaRef}
          placeholder={placeholder || "Search"}
          className={styles.textareaInput}
          value={value}
          onChange={handleTextareaChange}
          onFocus={() => onFocus?.()}
          id={inputId}
        />
      </div>
    );
  }

  return (
    <div className={clsx(styles.container, className)} ref={compRef} onClick={(e) => onClick?.(e)}>
      <MagnifyingGlassIcon className={styles.searchIcon} />
      <input
        placeholder={placeholder || "Search"}
        className={styles.searchInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => onFocus?.()}
        id={inputId}
      />
      {!!onClose && <XIcon className={styles.closeIcon} onClick={handleClose} />}
    </div>
  );
}
